/**
 * Citations, on both sides of the substitution.
 *
 * The model emits closed-registry ids (`[S11]`, `[L1]`, `[R]`) and the server
 * resolves each one to `[1](#src "on the deck, slide 11")` as it pipes the
 * stream, so the label can never be model-authored. `resolveCitations` is the
 * same lookup client-side — it is what makes a raw id renderable if a frame
 * arrives unresolved, and it is where the display ordinals are defined:
 * order of first appearance, so the reader still counts 1, 2, 3.
 *
 * Neither direction rewrites inside code: a fenced block quoting `[S11]` is
 * showing the reader a marker, not citing one.
 */

import type { ChatSource } from '../types/chat.types'

/** The registry alphabet: deck, LinkedIn, record, founder answers, documents on file. */
const REGISTRY_ID_PATTERN = /\[([SLRFD][A-Za-z0-9]*)\]/g

/** A resolved marker, as the server writes it and the renderer reads it. */
const RESOLVED_CITATION_PATTERN = /\[(\d+)\]\(#src "([^"]*)"\)/g

/**
 * Fenced blocks first, then inline spans. A fence left open by a stream still
 * in flight runs to the end of the text — otherwise every id after a half-typed
 * ``` would be rewritten and then un-rewritten when the fence closes.
 */
const CODE_PATTERN = /```[\s\S]*?(?:```|$)|`[^`\n]*`/g

/** A citation as it reads in the answer: a display ordinal and its label. */
export interface AnswerCitation {
  ordinal: number
  label: string
}

/** The same, still carrying the registry id it was resolved from. */
export interface ResolvedCitation extends AnswerCitation {
  id: string
}

export interface ResolvedAnswer {
  text: string
  citations: ResolvedCitation[]
}

export function resolveCitations(text: string, sources: readonly ChatSource[]): ResolvedAnswer {
  const registry = new Map(sources.map((source) => [source.id, source]))
  const citations: ResolvedCitation[] = []
  const ordinals = new Map<string, number>()

  const resolved = mapOutsideCode(text, (segment) =>
    segment.replace(REGISTRY_ID_PATTERN, (marker, id: string) => {
      const source = registry.get(id)
      // An id that is not in the registry is left exactly as the model wrote
      // it. Inventing a label for it is the one thing the registry exists to
      // prevent.
      if (source === undefined) return marker
      let ordinal = ordinals.get(id)
      if (ordinal === undefined) {
        ordinal = ordinals.size + 1
        ordinals.set(id, ordinal)
        citations.push({ id, ordinal, label: source.label })
      }
      return `[${ordinal}](#src "${source.label.replace(/"/g, "'")}")`
    })
  )

  return { text: resolved, citations }
}

/**
 * The citations already resolved into a body, in ordinal order. Used to build
 * the clipboard's source list from the answer the investor actually read.
 */
export function extractCitations(text: string): AnswerCitation[] {
  const citations: AnswerCitation[] = []
  const seen = new Set<number>()

  for (const part of splitCode(text)) {
    if (part.isCode) continue
    for (const match of part.text.matchAll(RESOLVED_CITATION_PATTERN)) {
      const ordinal = Number(match[1])
      if (seen.has(ordinal)) continue
      seen.add(ordinal)
      citations.push({ ordinal, label: match[2] ?? '' })
    }
  }

  return citations.sort((a, b) => a.ordinal - b.ordinal)
}

/** Strip the markdown link off every resolved citation, leaving `[1]`. */
export function toPlainCitations(text: string): string {
  return mapOutsideCode(text, (segment) => segment.replace(RESOLVED_CITATION_PATTERN, '[$1]'))
}

/**
 * Drops the bullets `reattest` contradicted, whole, BY INDEX. A bullet it
 * could not confirm is not shown with a warning beside it — it is not shown at
 * all, because a claim beside a "this may not be true" note is still a claim
 * the investor reads.
 *
 * The indices come from the server, which knows which bullet each attestation
 * read. Matching on the contested citation ordinal instead retracted every
 * bullet citing that slide: live (SeedForge, team question) slide 8 supported
 * the first bullet and not the third, and the reader lost all four bullets
 * citing it.
 *
 * A bullet is its `- ` line plus any `> ` quote lines immediately under it
 * (verify.py's own bullet_block shape: one list line, then its quotes), and
 * the index counts those groups in render order — the same order the ledger
 * numbers the bullets it kept. Only list items are ever dropped: the lead
 * lines and the "Not on record" line are not bullets and carry no index.
 */
export function retractBullets(text: string, retractedIndices: readonly number[]): string {
  if (retractedIndices.length === 0) return text
  const retracted = new Set(retractedIndices)
  let bulletIndex = -1
  const lines = text.split('\n')
  const kept: string[] = []
  let index = 0
  let inFence = false

  while (index < lines.length) {
    const line = lines[index] ?? ''
    if (/^```/.test(line)) inFence = !inFence
    if (inFence || !line.startsWith('- ')) {
      kept.push(line)
      index++
      continue
    }
    let end = index + 1
    while (end < lines.length && (lines[end] ?? '').startsWith('> ')) end++
    const group = lines.slice(index, end)
    bulletIndex++
    if (!retracted.has(bulletIndex)) kept.push(...group)
    index = end
  }

  return kept.join('\n')
}

interface TextPart {
  text: string
  isCode: boolean
}

function splitCode(text: string): TextPart[] {
  const parts: TextPart[] = []
  let index = 0
  for (const match of text.matchAll(CODE_PATTERN)) {
    parts.push({ text: text.slice(index, match.index), isCode: false })
    parts.push({ text: match[0], isCode: true })
    index = match.index + match[0].length
  }
  parts.push({ text: text.slice(index), isCode: false })
  return parts
}

function mapOutsideCode(text: string, map: (segment: string) => string): string {
  return splitCode(text)
    .map((part) => (part.isCode ? part.text : map(part.text)))
    .join('')
}
